import { forwardRef } from "react";
import { chain } from "react-aria";
import {
  type TextAreaProps as TextAreaPrimitiveProps,
  TextField as TextFieldPrimitive,
  type TextFieldProps as TextFieldPrimitiveProps,
} from "react-aria-components";
import { useControlledState } from "@react-stately/utils";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./TextAreaV2.styled";
import { OuterField } from "./OuterField";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import { CharactersCounter } from "../CharactersCounter";

export type TextAreaV2Props = PropsWithOptionalTestID &
  Pick<TextAreaPrimitiveProps, "placeholder"> &
  Omit<TextFieldPrimitiveProps, "children"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    autoGrow?: boolean;
    maxHeight?: number;
    withCharacterCounter?: boolean;
  };

const TextAreaV2 = forwardRef<HTMLTextAreaElement, TextAreaV2Props>(
  (props, ref) => {
    const {
      labelProps,
      alerts,
      placeholder,
      autoGrow,
      maxHeight,
      withCharacterCounter,
      onChange,
      testID = "textarea",
      ...otherProps
    } = props;

    const { maxLength } = otherProps;

    const [inputValue, setInputValue] = useControlledState(
      props.value,
      props.defaultValue ?? ""
    );

    const handleChange = chain(onChange, setInputValue);

    return (
      <TextFieldPrimitive {...otherProps} onChange={handleChange}>
        <InputWrapper
          alert={alerts && <InputAlerts alerts={alerts} />}
          gap={withCharacterCounter && maxLength !== undefined ? "4px" : "8px"}
        >
          <S.CharactersCounterContainer>
            <InputLabelTopV2 {...labelProps}>
              <OuterField>
                <S.TextArea
                  ref={ref}
                  $autoGrow={autoGrow}
                  $maxHeight={maxHeight}
                  placeholder={placeholder}
                  data-testid={testID}
                />
              </OuterField>
            </InputLabelTopV2>

            {withCharacterCounter && maxLength !== undefined && (
              <CharactersCounter
                length={inputValue.length}
                maxLength={maxLength}
                testID={`${testID}-character-counter`}
              />
            )}
          </S.CharactersCounterContainer>
        </InputWrapper>
      </TextFieldPrimitive>
    );
  }
);

TextAreaV2.displayName = "TextAreaV2";

export default TextAreaV2;
